// Downgrade and cancel confirmations for the Plan page. Same rules as the
// shared subscription modals: show what the target plan allows, warn when the
// church has more members than it permits, and explain the Song Studio
// retention window when chord detection or stems go away.
import { useState } from 'react';
import { Banner, Button, Modal, cx } from '@app/components/ui';
import { canAccessApiKeys } from '@app/api/subscription';
import { displayTierName, getTierFeatures, getTierLimits } from '@app/utils/tierUtils';

export const SONG_STUDIO_RETENTION_DAYS = 30;

const longDate = (iso?: string, fallback = 'the end of your billing period') =>
  iso ? new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : fallback;
const limit = (n: number, unlimitedAt: number) => (n >= unlimitedAt ? 'Unlimited' : String(n));

function PlanCompare({ from, to }: { from: string; to: string }) {
  const a = getTierLimits(from);
  const b = getTierLimits(to);
  return (
    <div className="grid grid-cols-2 gap-2.5">
      {[
        { label: 'Current plan', tier: from, l: a },
        { label: 'New plan', tier: to, l: b },
      ].map((c) => (
        <div key={c.label} className="rounded-[14px] bg-fill px-3.5 py-3">
          <div className="text-[12px] text-t4">{c.label}</div>
          <div className="text-[16px] font-semibold mt-0.5">{displayTierName(c.tier)}</div>
          <div className="text-[12.5px] text-t3 mt-1.5">
            {limit(c.l.maxMembers, 999999)} members · {limit(c.l.maxPlans, 999)} plans
          </div>
        </div>
      ))}
    </div>
  );
}

export function DowngradeModal({
  open,
  onClose,
  onConfirm,
  currentTier,
  targetTier,
  memberCount,
  chordUploadCount = 0,
  periodEnd,
  busy,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  currentTier: string;
  targetTier: string;
  memberCount: number;
  chordUploadCount?: number;
  periodEnd?: string;
  busy: boolean;
}) {
  const target = getTierLimits(targetTier);
  const excess = memberCount - target.maxMembers;
  const now = getTierFeatures(currentTier);
  const next = getTierFeatures(targetTier);
  const losesChordAi = now.chordAi && !next.chordAi;
  const losesStems = now.audioSeparation && !next.audioSeparation;
  const songs = chordUploadCount > 0 ? `your ${chordUploadCount} analyzed song${chordUploadCount === 1 ? '' : 's'}` : 'any analyzed songs';
  const retention = new Date(Date.now() + SONG_STUDIO_RETENTION_DAYS * 86400000).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const end = longDate(periodEnd, 'your billing period ends');

  return (
    <Modal open={open} onClose={onClose} title={`Switch to ${displayTierName(targetTier)}?`} width={540} dismissible={!busy}>
      <div className="flex flex-col gap-3.5">
        <PlanCompare from={currentTier} to={targetTier} />
        <p className="text-[13.5px] text-t3 leading-[1.55]">
          You keep {displayTierName(currentTier)} until <strong className="text-t1">{end}</strong>. After that your church moves to {displayTierName(targetTier)}.
        </p>
        {excess > 0 && (
          <Banner tone="acc" role="alert">
            <strong>{memberCount} members</strong> is more than {displayTierName(targetTier)} allows ({target.maxMembers}). Remove {excess} member{excess === 1 ? '' : 's'} from
            Members before {end}.
          </Banner>
        )}
        {(losesChordAi || losesStems) && (
          <Banner tone="amb">
            {losesChordAi ? (
              <>
                {displayTierName(targetTier)} doesn't include Song Studio. {songs[0].toUpperCase() + songs.slice(1)}, their audio and stems are locked, not deleted, until{' '}
                <strong>{retention}</strong>. Re-subscribe before then to get everything back.
              </>
            ) : (
              <>
                {displayTierName(targetTier)} doesn't include stem separation. Stems for {songs} are hidden until <strong>{retention}</strong>, then deleted. Chord charts stay.
              </>
            )}
          </Banner>
        )}
        <div className="text-[13px] text-t3 leading-[1.55]">
          <div className="font-semibold text-t1 mb-1">Right away</div>
          New members are capped at {target.maxMembers >= 999999 ? 'unlimited' : target.maxMembers} and service plans at {target.maxPlans >= 999 ? 'unlimited' : target.maxPlans}.
          Everyone already in the church stays.
        </div>
        <div className="flex gap-2.5 mt-1.5">
          <Button variant="ghost" shape="form" size="lg" onClick={onClose} disabled={busy}>
            Keep {displayTierName(currentTier)}
          </Button>
          <Button shape="form" size="lg" className="flex-1" loading={busy} onClick={onConfirm}>
            Switch plan
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export function CancelPlanModal({
  open,
  onClose,
  onConfirm,
  currentTier,
  memberCount,
  periodEnd,
  busy,
  error,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (immediate: boolean) => void;
  currentTier: string;
  memberCount: number;
  periodEnd?: string;
  busy: boolean;
  error?: string | null;
}) {
  const [when, setWhen] = useState<'period-end' | 'immediate'>('period-end');
  const [ack, setAck] = useState(false);
  const free = getTierLimits('free');
  const excess = memberCount - free.maxMembers;
  const needsAck = excess > 0;
  const losesApi = canAccessApiKeys({ tier: currentTier as 'free' | 'basic' | 'founders' | 'pro' | 'enterprise' });
  const end = longDate(periodEnd);
  const close = () => {
    setWhen('period-end');
    setAck(false);
    onClose();
  };
  const support = `mailto:support@churchnow.co?subject=${encodeURIComponent('Help canceling my subscription')}&body=${encodeURIComponent('I tried to cancel my subscription but it failed. Please help me cancel it.')}`;

  const options = [
    { v: 'period-end' as const, t: 'At the end of this period', d: `Keep ${displayTierName(currentTier)} until ${end}. You won't be charged again.` },
    { v: 'immediate' as const, t: 'Right now', d: `Move to Free immediately. This period isn't refunded.` },
  ];

  return (
    <Modal open={open} onClose={close} title="Cancel your plan?" subtitle={`Your church returns to Free (${free.maxMembers} members, ${free.maxPlans} plans).`} width={500} dismissible={!busy}>
      <div className="flex flex-col gap-3.5">
        <div role="radiogroup" aria-label="When to cancel" className="flex flex-col gap-2">
          {options.map((o) => {
            const on = when === o.v;
            return (
              <button
                key={o.v}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => setWhen(o.v)}
                className={cx('flex items-start gap-3 text-left px-3.5 py-3 rounded-[13px] border', on ? 'border-[rgba(208,48,69,.45)] bg-acc-bg' : 'border-line2 bg-fill hover:bg-fill2')}
              >
                <span className={cx('mt-0.5 w-[18px] h-[18px] rounded-full border-[1.5px] flex items-center justify-center shrink-0', on ? 'border-acc' : 'border-t5')}>
                  <span className={cx('w-2 h-2 rounded-full', on ? 'bg-acc' : '')} />
                </span>
                <span>
                  <span className="block text-[14px] font-semibold">{o.t}</span>
                  <span className="block text-[12.5px] text-t4 mt-px">{o.d}</span>
                </span>
              </button>
            );
          })}
        </div>
        <ul className="text-[13px] text-t3 leading-[1.6] list-disc pl-5">
          <li>Paid features stop working</li>
          {losesApi && <li>API keys and integrations stop working</li>}
          <li>You can subscribe again any time</li>
        </ul>
        {needsAck && (
          <Banner tone="acc">
            <div>
              {memberCount} members is more than Free allows ({free.maxMembers}). {excess} member{excess === 1 ? '' : 's'} will be removed when the plan ends.
            </div>
            <label className="flex items-start gap-2 mt-2 text-t1 cursor-pointer">
              <input type="checkbox" className="mt-[3px] accent-[var(--acc)]" checked={ack} onChange={(e) => setAck(e.target.checked)} disabled={busy} />
              <span>I understand this can't be undone</span>
            </label>
          </Banner>
        )}
        {error && (
          <Banner tone="acc" role="alert">
            {error}{' '}
            <a href={support} className="underline font-semibold">
              Email support
            </a>
          </Banner>
        )}
        <div className="flex gap-2.5 mt-1.5">
          <Button variant="ghost" shape="form" size="lg" onClick={close} disabled={busy}>
            Keep plan
          </Button>
          <Button shape="form" size="lg" className="flex-1" loading={busy} disabled={needsAck && !ack} onClick={() => onConfirm(when === 'immediate')}>
            Cancel plan
          </Button>
        </div>
      </div>
    </Modal>
  );
}
